import { TouchDblclickHandler } from "../components/touchDblclickHandler"
import { IOmniaSettings } from "../types"
import { addListener, isArray, isFunction } from "../utils/utils"
import { createDefaultBasemaps } from "./basemaps/default"
import { BasemapGroup } from "./basemaps/group"
import { MapGeolocation } from "./geolocation"
import { Layer } from "./layer"
import { RotateControl } from "./rotateControl"
import { IBasemapGroup, IMap, IMapContext, IMapTooltip, MapViewState } from "./types"
import { getGeometryCenter, isExtent } from "./utils"
import { ol_Coordinate, ol_Extent, ol_extent, ol_Feature, ol_geom, ol_layer, ol_interaction, ol_Map, ol_View, ol_FeatureLike, ol_control, ol_proj } from "@omnia/exports/openlayers";
import * as ol_proj4 from "ol/proj/proj4"

declare var proj4: any;

proj4.defs("EPSG:27700", "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs");

ol_proj4.register(proj4);

export function getDefaultExtent(): ol_Extent {
    return [-2015491.5618235273, 5969426.160959125, 1262128.2110448303, 8364045.383077126]
}

/** @internal */
export class Map implements IMap {

    private _dispose: Function[]
    private _disposed = false

    private $timeout: ng.ITimeoutService

    private map: ol_Map

    private _basemaps: BasemapGroup

    private _extent: ol_Extent
    private _target: HTMLElement

    private _layers: Layer[]
    private _customBasemapsGroup: ol_layer.Group
    private _picturesLayerGroup: ol_layer.Group

    private _enableContextMenu: boolean = true

    private _tooltipProviders: ((tooltip: IMapTooltip) => boolean)[] = []
    private _tooltip: IMapTooltip
    private _tooltipTimeout: ng.IPromise<any>
    private _tooltipWatchers: ((tooltip: IMapTooltip) => void)[] = []

    private _touchMenuHandler: TouchDblclickHandler

    private geoloc: MapGeolocation

    mapViewCssClass: string

    enableTooltips = true
    tooltipDelay = 100


    constructor(ctxt: IMapContext, private settings: IOmniaSettings, private defaultExtentProvider: () => any) {

        this.$timeout = ctxt.$timeout

        const view = new ol_View({
            minZoom: 5,
            maxZoom: 22
        })

        this.map = new ol_Map({

            layers: [],

            view,

            controls: ol_control.defaults({
                rotate: false
            }).extend([
                new ol_control.ScaleLine(),
                RotateControl.create(),
            ]),

            interactions: ol_interaction.defaults({
                doubleClickZoom: false
            }),

            keyboardEventTarget: window.document,

        })

        this.geoloc = new MapGeolocation(this.$timeout, this.map)

        this.setupTooltip()

        this.setupContextMenu()

        this.setupBasemaps(ctxt)

    }

    dispose() {

        if (this.geoloc) {
            this.geoloc.dispose()
            this.geoloc = null
        }

        if (this._touchMenuHandler) {
            this._touchMenuHandler.dispose()
            this._touchMenuHandler = null
        }

        if (this._dispose) {
            this._dispose.forEach(d => d())
        }

        if (this.map) {
            this.map.setTarget(null)
        }

        this._target = null

        this._disposed = true

    }

    private setupBasemaps(ctxt: IMapContext) {

        {

            // Preset basemaps

            const targetLayers = this.map.getLayers()
            this._basemaps = createDefaultBasemaps(targetLayers, ctxt.bingKey, this.settings)

        }

        this._customBasemapsGroup = new ol_layer.Group()
        this.map.addLayer(this._customBasemapsGroup)

        this._picturesLayerGroup = new ol_layer.Group()
        this.map.addLayer(this._picturesLayerGroup)

    }

    getMap(): ol_Map {
        return this.map
    }

    getView(): ol_View {
        return this.map.getView()
    }

    get basemaps(): IBasemapGroup {
        return this._basemaps
    }

    get customBasemapsGroup() {
        return this._customBasemapsGroup
    }

    get pictureLayersGroup() {
        return this._picturesLayerGroup
    }

    get layers(): Layer[] {
        return this._layers.slice()
    }

    set layers(value: Layer[]) {

        if (isArray(this._layers)) {

            this._layers.forEach((l) => {
                l.removeFromMap(this)
            })

        }

        this._layers = value.slice()

        if (isArray(this._layers)) {

            this._layers.forEach((l) => {
                l.addToMap(this)
            })

        }

        this.clearTooltip()

    }

    get tracking() {
        return this.geoloc && this.geoloc.tracking
    }

    setTracking(value: boolean, trace: boolean) {
        this.geoloc.setTracking(value, trace)
    }

    getTrackingInfo() {
        return this.geoloc.getTrackingInfo()
    }

    addLayer(layer: Layer) {
        this._layers.push(layer)
        layer.addToMap(this)
        this.clearTooltip()
    }

    removeLayer(layer: Layer) {
        layer.removeFromMap(this)
        this._layers.remove(l => l == layer)
        this.clearTooltip()
    }

    setTopmostLayer(layer: Layer) {
        this.removeLayer(layer)
        this.addLayer(layer)
    }

    setTarget(targetElement: HTMLElement, viewExtent?: boolean) {

        this._target = targetElement

        this.map.setTarget(targetElement)

        if (targetElement) {

            this._basemaps.activate()

            if (this._extent && (viewExtent !== false)) {
                this.viewExtentOnTimeout(50)
            }

        }

        this.clearTooltip()

    }

    private viewExtentOnTimeout(maxIterations: number) {

        if (!maxIterations) return

        this.$timeout(100).then(() => {

            const extent = this._extent

            if (!extent) {
                return
            }

            const size = this.map.getSize()

            if (size && isArray(size) && size[0] && size[1]) {
                // console.log("viewExtent", extent, new Error().stack)
                this.viewExtent(extent)
            }
            else {
                this.viewExtentOnTimeout(maxIterations - 1)
            }

        })

    }

    refresh() {

        this.map.render()

    }

    getViewState() {
        const view = this.map.getView()
        return {
            center: view.getCenter(),
            resolution: view.getResolution()
        }
    }

    setViewState({ center, resolution }: MapViewState) {
        const view = this.map.getView()
        view.setResolution(resolution)
        view.setCenter(center)
    }

    getCurrentViewExtent(): ol_Extent {
        const view = this.map.getView()
        return view.calculateExtent(this.map.getSize())
    }

    viewExtent(extent: ol_Extent | ol_geom.Geometry) {

        if (!extent) return

        this.clearTooltip()

        this._extent = extent = extent instanceof ol_geom.Geometry
            ? extent.getExtent()
            : extent

        const size = this.map.getSize()

        if (this._target && isArray(size) && (!!size[0]) && (!!size[1])) {

            const view = this.map.getView()

            view.fit(extent as any, { size })

            return view.calculateExtent(size)


        }

    }

    setCenter(center: ol_Extent | ol_Feature | ol_geom.Geometry | ol_Coordinate) {

        let coord: ol_Coordinate

        if (isExtent(center)) {
            coord = ol_extent.getCenter(center)
        }
        else if (center instanceof ol_Feature) {
            coord = getGeometryCenter(center.getGeometry())
        }
        else if (center instanceof ol_geom.Geometry) {
            coord = getGeometryCenter(center)
        }
        else {
            coord = <number[]>center
        }

        this.map.getView().setCenter(coord)

    }

    get enableContextMenu() {
        return this._enableContextMenu
    }

    set enableContextMenu(value: boolean) {
        this._enableContextMenu = value
        if ((!value) && this._tooltip && this._tooltip.contextMenu) {
            this.setTooltipNow(null)
        }
    }

    get tooltip() {
        return this._tooltip
    }

    addTooltipProvider(provider: (tooltip: IMapTooltip) => boolean): Function {

        if (!this._tooltipProviders.find(p => p == provider)) {
            this._tooltipProviders.push(provider)
            return () => this._tooltipProviders.remove(p => p == provider)
        }

    }

    clearTooltip() {

        const tt = this._tooltip

        if (!tt) return

        if ((!tt.contextMenu) || (!isFunction(tt.canClose)) || tt.canClose()) {
            this.setTooltipNow(null)
        }

    }

    showToolTipAtScreenPosition(x: number, y: number) {
        const tooltip = this.setupTooltipInternal([x, y], false, false, undefined)
        this.setTooltip(tooltip, 0)
    }

    private setupTooltip() {

        this.map.on("pointermove", evt => {

            if (evt.dragging) {
                this.setTooltip(null, 0)
                return
            }

            if (!this.enableTooltips) {
                this.setTooltip(null, 0)
                return
            }

            this.onTimeout(this.tooltipDelay, false, () => {

                const mouseEvent = evt.originalEvent
                const pixel = evt.pixel

                const tooltip = this.setupTooltipInternal(pixel, false, false, mouseEvent)
                this.setTooltip(tooltip)

            })

        })

    }

    setTooltip(tooltip: IMapTooltip, delay: number = this.tooltipDelay) {

        if (tooltip != this._tooltip) {

            this.onTimeout(delay, true, () => {
                this.setTooltipNow(tooltip)
            })

        }

    }

    private setTooltipNow(tooltip: IMapTooltip) {

        if (tooltip != this._tooltip) {

            const a = tooltip
            const b = this._tooltip

            this._tooltip = tooltip

            for (const w of this._tooltipWatchers) {
                w(a)
            }

        }

    }

    watchTooltip(watcher: (tooltip: IMapTooltip) => void) {

        this._tooltipWatchers.push(watcher)

        return () => {
            this._tooltipWatchers.remove(w => w == watcher)
        }

    }

    private onTimeout(delay: number, apply: boolean, handler: Function) {

        if (this._tooltipTimeout) {
            this.$timeout.cancel(this._tooltipTimeout)
            this._tooltipTimeout = null
        }

        this._tooltipTimeout = this.$timeout(() => {
            this._tooltipTimeout = null
            if (!this._disposed) {
                handler()
            }
        }, delay, apply)

    }

    private setupContextMenu() {

        const viewport = this.map.getViewport()

        this._touchMenuHandler = new TouchDblclickHandler(e => {

            if (e) {
                this.handleContextMenu(e, false)
            }
            else {
                this.clearTooltip()
            }

        })

        this._dispose = [
            addListener(viewport, "contextmenu", (e: MouseEvent) => this.handleContextMenu(e, false)),
            addListener(viewport, "dblclick", (e: MouseEvent) => this.handleContextMenu(e, true)),
            addListener(viewport, "touchstart", e => this._touchMenuHandler.handleTouchStart(e))
        ]

    }

    private handleContextMenu(e: MouseEvent, dblClick: boolean) {

        if (e.preventDefault) {
            e.preventDefault()
        }

        if (!this._enableContextMenu) {
            return
        }

        const pixel = this.map.getEventPixel(e)

        const tooltip = this.setupTooltipInternal(pixel, true, dblClick, e)

        this.setTooltip(tooltip, 0)

    }

    private setupTooltipInternal(pixel: number[], contextMenu: boolean, dblClick: boolean, event: MouseEvent): IMapTooltip {

        let tooltip: IMapTooltip

        this.map.forEachFeatureAtPixel(pixel, (feature, olayer) => {

            let layer: Layer = feature.get("layer")

            if ((contextMenu || olayer) && layer && (layer instanceof Layer) && layer.isVisible) {

                tooltip = this.getTooltipWithTemplate(pixel, contextMenu, dblClick, event, feature)

                return !!tooltip

            }

        })

        if ((!tooltip) && contextMenu) {
            tooltip = this.getTooltipWithTemplate(pixel, true, dblClick, event, null)
        }

        return (tooltip && tooltip.template ? tooltip : null)

    }

    private getTooltipWithTemplate(pixel: number[], contextMenu: boolean, dblClick: boolean, event: MouseEvent, feature: ol_FeatureLike): IMapTooltip {

        let tooltip = this.getTooltip(pixel, contextMenu, dblClick, event, feature)

        if (tooltip) {

            this._tooltipProviders.forEach(provider => {

                provider(tooltip)

                if (tooltip.template) {
                    return true
                }


            })

            if (tooltip.template) {
                return tooltip.template == "-" ? null : tooltip
            }

        }

    }

    private getTooltip(pixel: number[], contextMenu: boolean, dblClick: boolean, event: MouseEvent, feature: ol_FeatureLike): IMapTooltip {

        var px = pixel[0]
        var py = pixel[1]

        var delta = contextMenu ? -15 : 10

        px += delta
        py += delta

        pixel = [px, py]

        if (this._tooltip && (this._tooltip.feature == feature) && this._tooltip.screenPosition) {

            let currentPixel = this._tooltip.screenPosition

            let dx = Math.abs(currentPixel[0] - px)
            let dy = Math.abs(currentPixel[1] - py)

            if ((dx < 10) || (dy < 10)) {
                pixel = currentPixel
            }

        }

        let coord = this.map.getCoordinateFromPixel(pixel)

        return {

            screenPosition: pixel,
            mapPosition: coord,

            contextMenu: contextMenu,
            dblClick: dblClick,

            shiftKey: event && event.shiftKey,
            ctrlKey: event && event.ctrlKey,
            altKey: event && event.altKey,

            template: null,
            feature,
            data: (feature ? feature.get("data") : null),

        }


    }

    public viewDefaultExtent() {

        let extent = this.defaultExtentProvider()

        // console.log("viewDefaultExtent", extent, new Error().stack)

        if (extent) {
            this.viewExtent(extent)
        }

    }

    public updateSize() {
        this.map.updateSize()
        this._basemaps.updateSize()
    }

    public sortByStackOrder<T>(layers: T[], selector: (arg: T) => Layer): T[] {

        const olayers = this.map.getLayers().getArray()

        return layers
            .map(layer => {

                const index = olayers.indexOf(selector(layer)?.mapLayer)

                return {
                    layer,
                    z: (index < 0) ? olayers.length : index
                }

            })
            .sort((a, b) => b.z - a.z)
            .map(i => i.layer)

    }
}

